// ───────────────────────────────────────────────────────────────────
// Overview roll-up — worst verdict across POPULATED sections + the design's
// per-section ROW LIST. The promoted exception banner is rendered by the parent
// viewer ABOVE this. Placeholder sections show "Pending", never 0/fault.
//
// ME-CARDS-R1 A5 (overview-model.PNG): the bare-value tile grid is replaced by
// rows — status dot (verdict tone) + section NAME + the section's own one-line
// verdict headline (payload, rendered AS-IS per B3) + the selected window tag on
// the right. Row click still selects the tab. Plus the machine-state chips
// (run_state / SKU) when the payload carries those snapshot figures.
// ───────────────────────────────────────────────────────────────────
import React from "react";

import type {
  Figure,
  MachineCardArtifactData,
} from "../../../../../../shared/components/machineEvidence";
import {
  VerdictChip,
  isPlaceholderTab,
  resolveCoverage,
  resolveVerdict,
} from "../../../../../../shared/components/machineEvidence";
import { VERDICT_TONE_COLORS } from "../../../../../../shared/components/machineEvidence";
import useTranslation from "../../../../../../shared/hooks/useTranslation";
import { dt } from "../../../../../../shared/themes/redesignTokens";
import { orderedSectionTabs, worstVerdictTab } from "../machineCardConstants";
import { eyebrow, figureSignalKey, sourceMapOf, useTabLabel } from "./sectionChrome";

/** Machine-state chips: signal key -> client chrome label key. Values are
 *  payload data rendered VERBATIM. */
const STATE_CHIPS: Record<string, { key: string; label: string }> = {
  run_state: { key: "chip.runState", label: "run_state" },
  current_sku: { key: "chip.sku", label: "sku" },
};

export const OverviewRollup: React.FC<{
  card: MachineCardArtifactData;
  onSelect: (slug: string) => void;
  /** label of the currently selected window — the row tag. */
  windowLabel?: string;
}> = ({ card, onSelect, windowLabel }) => {
  const { translate } = useTranslation("machineEvidence");
  const translateTabLabel = useTabLabel();
  const worst = worstVerdictTab(card);
  const worstVerdict = worst ? resolveVerdict(worst.verdict) : resolveVerdict(null);
  const sources = sourceMapOf(card);

  // Machine-state chips — scanned across sections; only figures that actually
  // resolve to a value are shown (never a dash dressed as a chip).
  const chips: { signalKey: string; value: string }[] = [];
  for (const tab of card.tabs ?? []) {
    for (const figure of tab.figures ?? []) {
      const key = figureSignalKey(figure as Figure, card, sources);
      if (!key || !STATE_CHIPS[key] || chips.some((c) => c.signalKey === key)) continue;
      const coverage = resolveCoverage(figure);
      if (!coverage.showsValue || coverage.value === null) continue;
      chips.push({ signalKey: key, value: String(coverage.value) });
    }
  }

  return (
    <div
      className="msc-overview-rollup"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: 12,
        borderRadius: dt.radiusSm,
        background: dt.headerBg,
        border: `1px solid ${dt.cardBorder}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={eyebrow}>{translate("overview.worstVerdict", { defaultValue: "Worst verdict" }) as string}</span>
        <VerdictChip resolution={worstVerdict} chipOnly />
        {worst ? <span style={{ fontSize: 12, color: dt.textSecondary }}>· {translateTabLabel(worst)}</span> : null}
      </div>

      {chips.length > 0 ? (
        <div className="msc-state-chips" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {chips.map((chip) => (
            <span
              key={chip.signalKey}
              className="msc-state-chip"
              data-chip-key={chip.signalKey}
              style={{
                fontFamily: dt.fontMono,
                fontSize: 11,
                color: dt.textSecondary,
                background: dt.inputBg,
                border: `1px solid ${dt.cardBorder}`,
                borderRadius: dt.radiusPill,
                padding: "2px 8px",
              }}
            >
              {`${translate(STATE_CHIPS[chip.signalKey].key, {
                defaultValue: STATE_CHIPS[chip.signalKey].label,
              }) as string}: ${chip.value}`}
            </span>
          ))}
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: "column" }}>
        {orderedSectionTabs(card).map((tab) => {
          const placeholder = isPlaceholderTab(tab);
          const verdict = resolveVerdict(tab.verdict);
          const tone = VERDICT_TONE_COLORS[verdict.tone];
          const headline = placeholder
            ? (translate("overview.pending", { defaultValue: "Pending" }) as string)
            : verdict.headlineIsFallback
            ? (translate("verdict.statusUnknown", { defaultValue: "Status unknown" }) as string)
            : verdict.headline;
          return (
            <button
              key={tab.part_slug}
              type="button"
              className="msc-overview-row"
              data-section-slug={tab.part_slug}
              onClick={() => onSelect(tab.part_slug)}
              style={{
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 4px",
                background: "transparent",
                border: "none",
                borderBottom: `1px solid ${dt.rowBorder}`,
                cursor: "pointer",
                fontFamily: dt.font,
              }}
            >
              <span
                aria-hidden
                data-verdict-tone={verdict.tone}
                style={{ width: 7, height: 7, borderRadius: "50%", background: tone.fg, flex: "0 0 auto" }}
              />
              <span
                style={{
                  fontFamily: dt.fontMono,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: dt.textPrimary,
                  flex: "0 0 auto",
                  minWidth: 96,
                }}
              >
                {translateTabLabel(tab)}
              </span>
              <span
                {...(placeholder ? { "data-placeholder": true } : {})}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  color: placeholder ? dt.textMuted : dt.textSecondary,
                  fontStyle: placeholder ? "italic" : "normal",
                }}
              >
                {headline}
              </span>
              {windowLabel ? (
                <span style={{ fontFamily: dt.fontMono, fontSize: 10, color: dt.textMuted, flex: "0 0 auto" }}>
                  {windowLabel}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default OverviewRollup;
